import { useMemo } from 'react'
import { Minus, Plus, Printer } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/app/components/ui/Button'
import { StepShell } from '@/app/components/ui/StepShell'
import { publishToGuest } from '@/app/lib/guestChannel'
import { renderQrDataUrl } from '@/app/lib/qr'
import { printSheet } from '@/app/lib/print'
import { encodeSession } from '@/app/lib/video'
import { enqueue, release, whileFiling } from '@/app/lib/local/queue'
import { writeSessionFolder, appendSessionFile } from '@/app/lib/local/sessions'
import { readPhoto } from '@/app/lib/local/watcher'
import { resolveSelected, useAppStore } from '@/app/store/useAppStore'
import { useFlowStore } from '@/app/store/useFlowStore'
import { shareUrlFor } from '@/core/session'
import type { ImageFile } from '@/core/types'

const FRONTEND_BASE = import.meta.env.VITE_FRONTEND_BASE ?? 'https://raiaphoto.tech'

/**
 * Step 3 — confirm and print.
 *
 * This is the only screen in the console that does something irreversible, so
 * it is deliberately the plainest: one large preview of exactly what will come
 * out, the copy count, and a single button. Nothing else competes with it.
 *
 * The ordering is the whole design. The QR goes up on the guest screen and the
 * sheet goes to the printer FIRST; the local archive, the upload and the video
 * all happen afterwards and none of them can delay paper. That was true in the
 * agent build and matters more now, because the upload is this tab's own work
 * rather than a background service's.
 *
 * The browser's print dialog is the one visible loss against citaria-print.exe
 * (see app/lib/print.ts). Two of its settings have to be right or the sheet is
 * rescaled and the print inset stops meaning inches, so the screen says which —
 * Chrome remembers both per destination, making it a first-print-of-the-evening
 * step rather than a per-guest one.
 */
export function PrintStep({ onBack, onDone }: { onBack: () => void; onDone: () => void }) {
  const settings = useAppStore((s) => s.settings)
  const target = useAppStore((s) => s.target)
  const files = useAppStore((s) => s.files)
  const {
    selected,
    sessionKey,
    rendered,
    chosenLayoutId,
    copies,
    setCopies,
    printing,
    setPrinting,
  } = useFlowStore()

  const chosen = rendered?.find((r) => r.layout.id === chosenLayoutId)
  const chosenFiles = useMemo(() => resolveSelected(files, selected), [files, selected])
  const shareUrl = sessionKey ? shareUrlFor(sessionKey, FRONTEND_BASE) : ''

  const onPrint = async () => {
    if (!chosen || !sessionKey || !settings) return

    setPrinting(true)
    try {
      // Show the guest their code while the sheet is being spooled, exactly as
      // the desktop app did — this is the moment they are waiting through.
      //
      // Only PUBLISHED, never opened. The guest window is opened deliberately
      // from the header control at the start of the evening; opening it from
      // here raised it over the console mid-print, and an operator running
      // without a second monitor got a popup they never asked for. If it is not
      // open the message goes nowhere, which is fine — the QR is on the sheet,
      // and the header icon already says the screen is closed.
      publishToGuest({ type: 'qr.show', dataUrl: await renderQrDataUrl(shareUrl), url: shareUrl })

      const orientation = chosen.result.width >= chosen.result.height ? 'Landscape' : 'Portrait'

      await printSheet({
        image: chosen.result.print,
        orientation,
        invertOrientation: settings.invertOrientation,
        inset: { x: settings.printInsetX, y: settings.printInsetY },
        copies,
      })

      // Everything past this point is off the guest's critical path: they have
      // their print and their QR, and the link resolves once the queue drains.
      const filed = sessionKey
      void whileFiling(() =>
        fileSession({
          sessionKey: filed,
          photos: chosenFiles,
          archive: chosen.result.archive,
          sheet: chosen.result.print,
          target,
          video: settings.videoEnabled,
        }),
      ).catch((error) => {
        // Reaching here means the session never made it into the queue: the
        // guest holds a QR that will not resolve. That used to be swallowed
        // silently, so it stays on screen until the operator dismisses it.
        toast.error(
          `Sesi ${filed} tidak masuk antrean unggah — ${
            error instanceof Error ? error.message : String(error)
          }. Foto tetap ada di folder kamera.`,
          { duration: Infinity },
        )
      })

      // Deliberately not "Tercetak": the browser does not say whether the
      // dialog was confirmed or cancelled, so claiming it printed would be a
      // guess about the one thing that costs paper.
      toast.success('Dikirim ke dialog cetak')
      onDone()
    } catch (error) {
      publishToGuest({ type: 'qr.hide' })
      toast.error(error instanceof Error ? error.message : 'Gagal mencetak')
    } finally {
      setPrinting(false)
    }
  }

  return (
    <StepShell
      title="Cetak"
      hint="Periksa sekali lagi sebelum mencetak."
      onBack={printing ? undefined : onBack}
      onNext={undefined}
      status={printing ? 'Membuka dialog cetak…' : undefined}
    >
      <div className="mx-auto grid max-w-[1000px] gap-6 lg:grid-cols-[1fr_320px]">
        <div className="grid place-items-center rounded-[16px] bg-raised p-4 ring-1 ring-line">
          {chosen ? (
            <img
              src={chosen.previewUrl}
              alt="Pratinjau cetak"
              className="max-h-[58vh] w-auto rounded-[8px] object-contain shadow-2xl"
              draggable={false}
            />
          ) : (
            <p className="p-12 text-ink-muted">Tidak ada pratinjau</p>
          )}
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <span className="text-[14px] text-ink-muted">Jumlah salinan</span>
            <div className="mt-2 flex items-center gap-3">
              <Button
                variant="secondary"
                size="lg"
                aria-label="Kurangi salinan"
                onClick={() => setCopies(copies - 1)}
                disabled={copies <= 1 || printing}
                className="w-[56px] px-0"
              >
                <Minus size={20} />
              </Button>
              <span
                aria-live="polite"
                className="min-w-[72px] text-center text-[34px] font-semibold tabular-nums"
              >
                {copies}
              </span>
              <Button
                variant="secondary"
                size="lg"
                aria-label="Tambah salinan"
                onClick={() => setCopies(copies + 1)}
                disabled={copies >= 99 || printing}
                className="w-[56px] px-0"
              >
                <Plus size={20} />
              </Button>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-y-2 rounded-[14px] bg-surface p-4 text-[14px] ring-1 ring-line">
            <dt className="text-ink-muted">Foto</dt>
            <dd className="text-right">{chosenFiles.length}</dd>
            <dt className="text-ink-muted">Kertas</dt>
            <dd className="text-right">4R</dd>
            <dt className="text-ink-muted">Halaman</dt>
            <dd className="text-right">{copies}</dd>
          </dl>

          <Button size="xl" block onClick={onPrint} disabled={!chosen || printing}>
            <Printer size={22} />
            {printing ? 'Mencetak…' : `Cetak ${copies > 1 ? `${copies}×` : ''}`}
          </Button>

          {/*
            The two dialog settings that decide whether the sheet comes out the
            right size. Chrome remembers them per destination, so this is read
            once at the start of an evening — but a wrong one wastes 4R paper,
            which is exactly the failure §9 warns about.
          */}
          <div className="rounded-[12px] bg-warn/10 p-3 text-[12px] text-warn ring-1 ring-warn/25">
            <p className="font-semibold">Di dialog cetak, pastikan:</p>
            <p className="mt-1">
              Kertas <strong>4×6 in</strong> · Margin <strong>None</strong> · Skala{' '}
              <strong>100%</strong> · Salinan <strong>1</strong>
            </p>
            <p className="mt-1 opacity-80">
              Jumlah salinan di atas sudah menjadi {copies} halaman.
            </p>
          </div>

          <p className="text-center text-[12px] text-ink-faint">
            Foto diunggah ke Raia setelah cetak, lalu QR tamu aktif otomatis.
          </p>
        </div>
      </div>
    </StepShell>
  )
}

/**
 * Everything that happens after the paper: the upload, the local archive and
 * the animation — in that order, and the order is the fix for a lost session.
 *
 * This used to encode first and queue last, so the guest's Drive folder would
 * hold what the desktop app's did in one job. It also meant a minute in which
 * the session existed only in this tab's memory, and on 2026-09-14 a printed
 * guest's link never resolved because the tab did not outlast it. Now the
 * session is durable in IndexedDB within a second or two of the print; the
 * archive and the encode happen after, and the video joins the queued job
 * through a hold (see `release` in local/queue.ts). The Drive folder ends up
 * the same; a tab closed mid-encode costs animate.mp4 instead of the session.
 */
async function fileSession(input: {
  sessionKey: string
  photos: ImageFile[]
  archive: Blob
  sheet: Blob
  target: FileSystemDirectoryHandle | null
  video: boolean
}): Promise<void> {
  const { sessionKey, target } = input

  const sources: { name: string; blob: Blob }[] = []
  for (const photo of input.photos) {
    const file = await readPhoto(photo)
    if (file) sources.push({ name: photo.fileName, blob: file })
  }

  const archiveName = `${sessionKey}.png`
  const animate = input.video && sources.length > 0

  // Only the archive composite goes up, not both. They are byte-identical
  // unless `archiveWithoutQr` is on, and sending a duplicate 4R PNG over venue
  // wifi is the same kind of waste as the `input.txt` that used to sync into
  // the customer's folder (§6).
  //
  // The one step allowed to throw out of here: if it fails the session is not
  // queued, and the caller has to say so loudly.
  await enqueue(sessionKey, [...sources, { name: archiveName, blob: input.archive }], {
    hold: animate,
  })

  let animation: Blob | null = null
  try {
    try {
      // The local copy keeps both composites, as the agent's session folder
      // did: `<key>.png` is the customer's archive and `print_<key>.png` is the
      // sheet that went to the printer. With `archiveWithoutQr` off they are the
      // same bytes, which is the WPF behaviour §6 documents.
      const { failed } = await writeSessionFolder(target, sessionKey, [
        ...sources,
        { name: archiveName, blob: input.archive },
        { name: `print_${archiveName}`, blob: input.sheet },
      ])
      if (failed.length > 0) toast.warning(`Arsip lokal tidak lengkap — ${failed.join(', ')}`)
    } catch (error) {
      // The upload is already queued; a folder that lost its grant only costs
      // the local copy, and must not also cost the animation.
      toast.warning(
        error instanceof Error ? `Arsip lokal gagal — ${error.message}` : 'Arsip lokal gagal',
      )
    }

    if (animate) {
      try {
        // `encodeSession` probes the encoder itself and throws with a reason if
        // H.264 is unavailable here, so there is no separate capability check
        // to keep in step with it.
        animation = await encodeSession(sources)
        await appendSessionFile(target, sessionKey, { name: 'animate.mp4', blob: animation })
      } catch (error) {
        // The guest already has a printed photo with a working QR. A failed
        // animation is a missing extra, not a failed session.
        toast.warning(
          error instanceof Error ? `Animasi dilewati — ${error.message}` : 'Animasi sesi gagal dibuat',
        )
      }
    }
  } finally {
    // Always, even if the encode threw: a job left held would sit out the whole
    // expiry before its photos started going up.
    if (animate) {
      // Never rethrown: the session is queued either way (release lifts the hold
      // even when its write fails), and letting this reject would reach the
      // caller's "not queued" error, which would be false.
      const attached = await release(
        sessionKey,
        animation ? [{ name: 'animate.mp4', blob: animation }] : [],
      ).catch(() => false)
      if (animation && !attached) {
        toast.warning('Animasi selesai terlambat — tidak ikut terunggah, tersimpan di arsip lokal')
      }
    }
  }
}
